import React, { useState, useEffect, useMemo, useContext, useRef } from "react";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { SelectLayer } from "../../components/CommonInputs";
import { MapitStateContext } from "src/states/MapitState";
import { MapitUtils } from "src/managers/MapitUtils";
import { JPBReferenceProperty, JPBAssumptions, AreaUnit, SoilQualityGroup, SOIL_QUALITY_GROUPS, getSoilTypeColor, AverageCalculationMethod } from "./JPBState";
import { formatPricePerSquareMeter, formatArea, formatPercentage, calculateSoilSpecificAgriculturalPrices, calculateAreaPrice, mapSoilTypeToGroup, calculateWeightedMeanForSeries, getResolvedJPBAssumptions, getSoilQualityFactorsByGroup, getNonAgricultureToAgricultureRatio } from "./JPBFunc";
import { area, bbox, combine, intersect, featureCollection } from "@turf/turf";
import { GlassInputGroup } from "../../components/GlassInput";
import { Spinner } from "react-bootstrap";
import { LayerVariabilityType } from "src/common/managers/Types";
import type { GeoJSONSource } from "maplibre-gl";
import { DataCollector } from "src/dataCollector/DataCollector";
import { autoUpdate, FloatingPortal, offset, useDismiss, useFloating, useInteractions } from "@floating-ui/react";
import { ActionTracker } from "src/common/managers/ActionTracker";

const SOURCE_ID_POLYGON_ANALYZER_FARMLAND = 'jpb-polygon-analyzer-farmland';
const SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT = 'jpb-polygon-analyzer-jordbundskort';
const LAYER_ID_POLYGON_ANALYZER_FARMLAND = 'jpb-polygon-analyzer-farmland-layer';
const LAYER_ID_POLYGON_ANALYZER_JORDBUNDSKORT = 'jpb-polygon-analyzer-jordbundskort-layer';

const yieldToMainThread = () => new Promise<void>((resolve) => {
   if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => resolve());
      return;
   }
   window.setTimeout(() => resolve(), 0);
});

function bboxesOverlap(a: [number, number, number, number], b: [number, number, number, number]): boolean {
   return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}

function toFarmlandParts(farmlandGeometry: any): Array<{ feature: any; bbox: [number, number, number, number] }> {
   if (!farmlandGeometry) return [];
   if (farmlandGeometry.type === "Polygon") {
      const feature = {
         type: "Feature" as const,
         geometry: farmlandGeometry,
         properties: {}
      };
      return [{ feature, bbox: bbox(feature as any) as [number, number, number, number] }];
   }
   if (farmlandGeometry.type === "MultiPolygon" && Array.isArray(farmlandGeometry.coordinates)) {
      return farmlandGeometry.coordinates.map((polygonCoords: any) => {
         const feature = {
            type: "Feature" as const,
            geometry: {
               type: "Polygon",
               coordinates: polygonCoords
            },
            properties: {}
         };
         return { feature, bbox: bbox(feature as any) as [number, number, number, number] };
      });
   }
   const fallbackFeature = {
      type: "Feature" as const,
      geometry: farmlandGeometry,
      properties: {}
   };
   return [{ feature: fallbackFeature, bbox: bbox(fallbackFeature as any) as [number, number, number, number] }];
}

async function runWithYield<T>(work: () => T): Promise<T> {
   await yieldToMainThread();
   const result = work();
   await yieldToMainThread();
   return result;
}

function getFarmlandLayerYear(transactionDate: Date): number {
   const transactionYear = transactionDate.getFullYear();
   const earliestAvailableYear = 2008;
   const latestAvailableYear = 2025;

   if (transactionYear >= earliestAvailableYear && transactionYear <= latestAvailableYear) {
      return transactionYear;
   }

   if (transactionYear > latestAvailableYear) {
      return latestAvailableYear;
   }

   if (transactionYear < earliestAvailableYear) {
      return earliestAvailableYear;
   }

   return earliestAvailableYear;
}

async function fetchFarmlandOverlap(
   geoshape: any,
   transactionDate: Date,
   _abortSignal?: AbortSignal
): Promise<any | null> {
   if (!geoshape) {
      return null;
   }

   const layerYear = getFarmlandLayerYear(transactionDate);
   const layerName = `Marker_${layerYear}` as any;

   try {
      const featureCollection = {
         type: "FeatureCollection" as const,
         features: [{
            type: "Feature" as const,
            geometry: geoshape,
            properties: {}
         }]
      };

      const result = await DataCollector.WFS.getFeatureIntersect(
         "FVM (Føde, Land og Fisk)",
         layerName,
         featureCollection as any,
         {}
      );

      if (!result || !result.features || result.features.length === 0) {
         return null;
      }

      const geometries = result.features.map((feature: any) => feature.geometry);

      if (geometries.length === 0) {
         return null;
      }

      const farmlandFeatureCollection = {
         type: "FeatureCollection" as const,
         features: geometries.map((geom: any) => ({
            type: "Feature" as const,
            geometry: geom,
            properties: {}
         }))
      };

      const combined = combine(farmlandFeatureCollection as any);

      if (combined && combined.features && combined.features.length > 0) {
         const firstFeature = combined.features[0] as any;
         return firstFeature?.geometry || null;
      }

      return null;
   } catch (error) {
      console.error("Error fetching farmland overlap:", error);
      throw error;
   }
}

function calculateFarmlandIntersection(
   geoshape: any,
   farmlandGeometry: any
): { intersectionGeometry: any; overlapPercentage: number } | null {
   if (!geoshape || !farmlandGeometry) {
      return null;
   }

   try {
      const combinedFeatureCollection = featureCollection([
         {
            type: "Feature" as const,
            geometry: geoshape,
            properties: {}
         },
         {
            type: "Feature" as const,
            geometry: farmlandGeometry,
            properties: {}
         }
      ]);

      const intersection = intersect(combinedFeatureCollection);

      if (!intersection) {
         return {
            intersectionGeometry: null,
            overlapPercentage: 0
         };
      }

      const intersectionGeometry = intersection.geometry;

      const intersectionArea = area(intersection);
      const propertyArea = area({
         type: "Feature" as const,
         geometry: geoshape,
         properties: {}
      });

      if (propertyArea === 0) {
         return null;
      }

      const overlapPercentage = (intersectionArea / propertyArea) * 100;

      return {
         intersectionGeometry,
         overlapPercentage
      };
   } catch (error) {
      console.error("Error calculating farmland intersection:", error);
      return null;
   }
}

async function calculateJordbundskortFarmlandIntersection(
   farmlandGeometry: any,
   jordbundskortGeometry: any
): Promise<{ intersectionGeometry: any | null; overlapPercentages: { [soilType: string]: number } } | null> {
   if (!farmlandGeometry || !jordbundskortGeometry) {
      return null;
   }

   try {
      if (jordbundskortGeometry.type !== "FeatureCollection" || !jordbundskortGeometry.features) {
         return null;
      }

      const intersectionFeatures: any[] = [];
      const overlapPercentages: { [soilType: string]: number } = {};
      const farmlandParts = toFarmlandParts(farmlandGeometry);
      if (!farmlandParts.length) {
         return null;
      }

      const farmlandArea = area({
         type: "Feature" as const,
         geometry: farmlandGeometry,
         properties: {}
      });

      if (farmlandArea === 0) {
         return null;
      }

      const jordbundskortFeatures = jordbundskortGeometry.features as any[];
      for (let i = 0; i < jordbundskortFeatures.length; i++) {
         // Yield frequently so the browser can paint and remain interactive.
         if (i > 0 && i % 2 === 0) {
            await yieldToMainThread();
         }
         const jordbundskortFeature = jordbundskortFeatures[i];
         if (!jordbundskortFeature.geometry) {
            continue;
         }

         try {
            const jordbundFeatureForIntersect = {
               type: "Feature",
               geometry: jordbundskortFeature.geometry,
               properties: {}
            } as any;
            const jordbundskortBBox = bbox(jordbundFeatureForIntersect);
            const candidateFarmlandParts = farmlandParts.filter((part) =>
               bboxesOverlap(part.bbox, jordbundskortBBox as [number, number, number, number])
            );
            if (!candidateFarmlandParts.length) {
               continue;
            }

            let totalFeatureIntersectionArea = 0;
            const featureIntersections: any[] = [];
            for (let j = 0; j < candidateFarmlandParts.length; j++) {
               const combinedFeatureCollection = featureCollection([
                  candidateFarmlandParts[j].feature,
                  jordbundFeatureForIntersect
               ]) as any;
               const intersection = intersect(combinedFeatureCollection as any);
               if (!intersection || !intersection.geometry) {
                  continue;
               }
               const intersectionArea = area(intersection);
               if (!(intersectionArea > 0)) {
                  continue;
               }
               totalFeatureIntersectionArea += intersectionArea;
               featureIntersections.push(intersection);
            }
            if (featureIntersections.length === 0 || totalFeatureIntersectionArea <= 0) {
               continue;
            }

            const overlapPercentage = (totalFeatureIntersectionArea / farmlandArea) * 100;

            const jordtype = jordbundskortFeature.properties?.Jordtype ||
               jordbundskortFeature.properties?.jordtype ||
               'Unknown';

            if (overlapPercentages[jordtype]) {
               overlapPercentages[jordtype] += overlapPercentage;
            } else {
               overlapPercentages[jordtype] = overlapPercentage;
            }

            featureIntersections.forEach((intersection) => {
               intersectionFeatures.push({
                  type: "Feature" as const,
                  geometry: intersection.geometry,
                  properties: {
                     ...jordbundskortFeature.properties
                  }
               });
            });
         } catch (error) {
            console.error("Error calculating intersection for jordbundskort feature:", error);
         }
      }

      if (intersectionFeatures.length === 0) {
         return null;
      }

      const totalPercentage = Object.values(overlapPercentages).reduce((sum, p) => sum + (typeof p === 'number' ? p : 0), 0);

      if (totalPercentage > 0 && Math.abs(totalPercentage - 100) > 0.01) {
         const normalizationFactor = 100 / totalPercentage;
         for (const jordtype in overlapPercentages) {
            if (typeof overlapPercentages[jordtype] === 'number') {
               overlapPercentages[jordtype] = overlapPercentages[jordtype] * normalizationFactor;
            }
         }
      }

      return {
         intersectionGeometry: {
            type: "FeatureCollection" as const,
            features: intersectionFeatures
         },
         overlapPercentages
      };
   } catch (error) {
      console.error("Error calculating jordbundskort-farmland intersection:", error);
      return null;
   }
}

async function fetchJordbundskortOverlap(
   geoshape: any,
   _abortSignal?: AbortSignal
): Promise<any | null> {
   if (!geoshape) {
      return null;
   }

   const layerName = "Jordbundskort_2024" as any;

   try {
      const featureCollection = {
         type: "FeatureCollection" as const,
         features: [{
            type: "Feature" as const,
            geometry: geoshape,
            properties: {}
         }]
      };

      const result = await DataCollector.WFS.getFeatureIntersect(
         "FVM (Føde, Land og Fisk)",
         layerName,
         featureCollection as any,
         {}
      );

      if (!result || !result.features || result.features.length === 0) {
         return null;
      }

      return {
         type: "FeatureCollection" as const,
         features: result.features.map((feature: any) => ({
            type: "Feature" as const,
            geometry: feature.geometry,
            properties: feature.properties || {}
         }))
      };
   } catch (error) {
      console.error("Error fetching jordbundskort overlap:", error);
      throw error;
   }
}

type JPBPolygonAnalyzerProps = {
   referenceProperties: JPBReferenceProperty[];
   checkedItems: Set<string>;
   areaUnit: AreaUnit;
   averageCalculationMethod: AverageCalculationMethod;
   customAssumptions?: Partial<JPBAssumptions>;
   settingsRefreshToken?: number;
}

type GeometryData = {
   polygonArea: number;
   farmlandOverlapArea: number;
   farmlandOverlapPercentage: number;
   soilComposition: { [soilType: string]: number };
   error?: string;
}

type AnalysisResult = {
   polygonArea: number;
   farmlandOverlapArea: number;
   farmlandOverlapPercentage: number;
   soilComposition: { [soilGroup: string]: number };
   estimatedPricePerSquareMeter: number | null;
   error?: string;
}

function SoilDistributionBar(props: {
   analysisResult: AnalysisResult;
   hoveredSoilType: string | null;
   hoveredSoilPercentage: number | null;
   hoveredNonFarmland: boolean;
   setHoveredSoilType: (type: string | null) => void;
   setHoveredSoilPercentage: (percentage: number | null) => void;
   setHoveredNonFarmland: (hovered: boolean) => void;
}) {
   const { analysisResult, hoveredSoilType, hoveredSoilPercentage, hoveredNonFarmland, setHoveredSoilType, setHoveredSoilPercentage, setHoveredNonFarmland } = props;
   const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
   const [nonFarmlandReferenceElement, setNonFarmlandReferenceElement] = useState<HTMLElement | null>(null);

   const { refs: tooltipRefs, floatingStyles: tooltipFloatingStyles, context: tooltipContext } = useFloating({
      whileElementsMounted: autoUpdate,
      open: hoveredSoilType !== null,
      middleware: [offset(8)],
      placement: 'top',
      elements: {
         reference: referenceElement
      }
   });

   const { refs: nonFarmlandTooltipRefs, floatingStyles: nonFarmlandTooltipFloatingStyles, context: nonFarmlandTooltipContext } = useFloating({
      whileElementsMounted: autoUpdate,
      open: hoveredNonFarmland,
      middleware: [offset(8)],
      placement: 'top',
      elements: {
         reference: nonFarmlandReferenceElement
      }
   });

   const { getFloatingProps: getTooltipFloatingProps } = useInteractions([
      useDismiss(tooltipContext)
   ]);

   const { getFloatingProps: getNonFarmlandTooltipFloatingProps } = useInteractions([
      useDismiss(nonFarmlandTooltipContext)
   ]);

   return (
      <div style={{ marginTop: "20px" }}>
         <div style={{ marginBottom: "10px", fontWeight: "600" }}>
            {Localization.getText("Area Distribution")}
         </div>
         <div style={{
            width: "100%",
            height: "30px",
            backgroundColor: "#e0e0e0",
            borderRadius: "4px",
            overflow: "hidden",
            display: "flex",
            position: "relative"
         }}>
            <div style={{
               width: `${analysisResult.farmlandOverlapPercentage}%`,
               backgroundColor: "#4CAF50",
               position: "relative",
               display: "flex"
            }}>
               {Object.entries(analysisResult.soilComposition).map(([soilType, percentage], index, arr) => {
                  const cumulativePercentage = arr.slice(0, index).reduce((sum, [, p]) => sum + p, 0);
                  const width = percentage;
                  const percentageOfTotal = (analysisResult.farmlandOverlapPercentage * percentage) / 100;
                  const soilColor = getSoilTypeColor(soilType);
                  const isHovered = hoveredSoilType === soilType;

                  return (
                     <div
                        key={soilType}
                        ref={(el) => {
                           if (isHovered && el) {
                              setReferenceElement(el);
                              tooltipRefs.setReference(el);
                           }
                        }}
                        style={{
                           position: "absolute",
                           left: `${cumulativePercentage}%`,
                           width: `${width}%`,
                           height: "100%",
                           backgroundColor: soilColor,
                           borderRight: index < arr.length - 1 ? "1px solid rgba(0,0,0,0.1)" : "none",
                           cursor: "pointer",
                           opacity: isHovered ? 0.9 : 1,
                           transition: "opacity 0.2s"
                        }}
                        onMouseEnter={(e) => {
                           setReferenceElement(e.currentTarget);
                           tooltipRefs.setReference(e.currentTarget);
                           setHoveredSoilType(soilType);
                           setHoveredSoilPercentage(percentageOfTotal);
                        }}
                        onMouseLeave={() => {
                           setHoveredSoilType(null);
                           setHoveredSoilPercentage(null);
                           setReferenceElement(null);
                        }}
                     />
                  );
               })}
            </div>
            <div
               ref={(el) => {
                  if (hoveredNonFarmland && el) {
                     setNonFarmlandReferenceElement(el);
                     nonFarmlandTooltipRefs.setReference(el);
                  }
               }}
               style={{
                  width: `${100 - analysisResult.farmlandOverlapPercentage}%`,
                  backgroundColor: "#9E9E9E",
                  cursor: "pointer",
                  opacity: hoveredNonFarmland ? 0.9 : 1,
                  transition: "opacity 0.2s"
               }}
               onMouseEnter={(e) => {
                  setNonFarmlandReferenceElement(e.currentTarget);
                  nonFarmlandTooltipRefs.setReference(e.currentTarget);
                  setHoveredNonFarmland(true);
               }}
               onMouseLeave={() => {
                  setHoveredNonFarmland(false);
                  setNonFarmlandReferenceElement(null);
               }}
            />
         </div>
         {hoveredSoilType !== null && hoveredSoilPercentage !== null && (
            <FloatingPortal>
               <div
                  ref={tooltipRefs.setFloating}
                  style={{
                     ...tooltipFloatingStyles,
                     zIndex: 1000,
                     backgroundColor: "rgba(0, 0, 0, 0.85)",
                     color: "white",
                     padding: "8px 12px",
                     borderRadius: "6px",
                     fontSize: "13px",
                     fontWeight: "500",
                     pointerEvents: "none",
                     boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
                  }}
                  {...getTooltipFloatingProps()}
               >
                  <div style={{ fontWeight: "600", marginBottom: "2px" }}>{hoveredSoilType}</div>
                  <div style={{ fontSize: "12px", opacity: 0.9 }}>
                     {Localization.getText("Percentage")}: {formatPercentage(hoveredSoilPercentage)}
                  </div>
               </div>
            </FloatingPortal>
         )}
         {hoveredNonFarmland && (
            <FloatingPortal>
               <div
                  ref={nonFarmlandTooltipRefs.setFloating}
                  style={{
                     ...nonFarmlandTooltipFloatingStyles,
                     zIndex: 1000,
                     backgroundColor: "rgba(0, 0, 0, 0.85)",
                     color: "white",
                     padding: "8px 12px",
                     borderRadius: "6px",
                     fontSize: "13px",
                     fontWeight: "500",
                     pointerEvents: "none",
                     boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
                  }}
                  {...getNonFarmlandTooltipFloatingProps()}
               >
                  <div style={{ fontWeight: "600", marginBottom: "2px" }}>
                     {Localization.getText("Non-Farmland")}
                  </div>
                  <div style={{ fontSize: "12px", opacity: 0.9 }}>
                     {Localization.getText("Percentage")}: {formatPercentage(100 - analysisResult.farmlandOverlapPercentage)}
                  </div>
               </div>
            </FloatingPortal>
         )}
         <div style={{ display: "flex", justifyContent: "space-between", marginTop: "5px", fontSize: "12px", color: "#666" }}>
            <span>
               {Localization.getText("Farmland")}: {formatPercentage(analysisResult.farmlandOverlapPercentage)}
            </span>
            <span>
               {Localization.getText("Non-Farmland")}: {formatPercentage(100 - analysisResult.farmlandOverlapPercentage)}
            </span>
         </div>
      </div>
   );
}

export function JPBPolygonAnalyzer(props: JPBPolygonAnalyzerProps) {
   const { state: mapitState } = useContext(MapitStateContext);
   const assumptions = useMemo(
      () => getResolvedJPBAssumptions(props.customAssumptions),
      [props.customAssumptions, props.settingsRefreshToken]
   );
   const soilFactorsByGroup = useMemo(() => getSoilQualityFactorsByGroup(assumptions), [assumptions]);
   const nonAgriToAgriRatio = useMemo(() => getNonAgricultureToAgricultureRatio(assumptions), [assumptions]);
   const [selectedLayerId, setSelectedLayerId] = useState<number | undefined>(undefined);
   const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
   const [isCalculating, setIsCalculating] = useState<boolean>(false);
   const [isFetchingGeometry, setIsFetchingGeometry] = useState<boolean>(false);
   const [geometryData, setGeometryData] = useState<GeometryData | null>(null);
   const [farmlandIntersectionGeometry, setFarmlandIntersectionGeometry] = useState<any | null>(null);
   const [jordbundskortIntersectionGeometry, setJordbundskortIntersectionGeometry] = useState<any | null>(null);
   const [hoveredSoilType, setHoveredSoilType] = useState<string | null>(null);
   const [hoveredSoilPercentage, setHoveredSoilPercentage] = useState<number | null>(null);
   const [hoveredNonFarmland, setHoveredNonFarmland] = useState<boolean>(false);
   const calculationTimeoutMs = useMemo(() => {
      const configured = Number(SettingsManager.getSystemSetting("jpb.polygonAnalyzerTimeoutMs", 90000));
      if (!Number.isFinite(configured)) return 90000;
      return Math.max(5000, Math.floor(configured));
   }, [props.settingsRefreshToken]);

   // Cache for layers that failed due to timeout - prevents retrying
   const failedLayersRef = useRef<Set<number>>(new Set());
   const timeoutRef = useRef<NodeJS.Timeout | null>(null);
   const trackedPolygonSignatureRef = useRef<string | null>(null);

   const polygonLayers = useMemo(() => {
      return Object.values(mapitState.layers)
         .filter(layer => layer.layerVariability !== LayerVariabilityType.FixedFeatureLayer)
         .filter(layer => {
            return MapitUtils.isAreaLayer(layer.type) ||
               layer.type === "GeoJSON_Polygon" ||
               (layer.geoJson && layer.geoJson.features && layer.geoJson.features.length > 0 &&
                  layer.geoJson.features[0]?.geometry?.type === "Polygon");
         });
   }, [mapitState.layers]);

   const selectedLayer = useMemo(() => {
      if (selectedLayerId === undefined) return null;
      return mapitState.layers[selectedLayerId] || null;
   }, [selectedLayerId, mapitState.layers]);

   const selectedPolygon = useMemo(() => {
      if (!selectedLayer || !selectedLayer.geoJson || !selectedLayer.geoJson.features || selectedLayer.geoJson.features.length === 0) {
         return null;
      }

      const polygonFeatures = selectedLayer.geoJson.features.filter((f: any) =>
         f.geometry && (f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon")
      );

      if (polygonFeatures.length === 0) {
         return null;
      }

      if (polygonFeatures.length === 1) {
         return polygonFeatures[0];
      }

      try {
         const featureCollectionToCombine = featureCollection(polygonFeatures);
         const combined = combine(featureCollectionToCombine as any);

         if (combined && combined.features && combined.features.length > 0) {
            const combinedGeometry = combined.features[0]?.geometry;
            if (combinedGeometry) {
               return {
                  type: "Feature" as const,
                  geometry: combinedGeometry,
                  properties: {}
               };
            }
         }
      } catch (error) {
         console.error("Error combining polygon features:", error);
         return polygonFeatures[0];
      }

      return polygonFeatures[0];
   }, [selectedLayer]);

   const checkedProperties = useMemo(
      () => props.referenceProperties.filter((p) => props.checkedItems.has(p.documentID)),
      [props.referenceProperties, props.checkedItems]
   );

   const avgEffectiveMarkPrice = useMemo(
      () =>
         calculateWeightedMeanForSeries(checkedProperties, "effectiveMark", {
            delta: nonAgriToAgriRatio,
            averageCalculationMethod: props.averageCalculationMethod
         }) ?? 0,
      [checkedProperties, nonAgriToAgriRatio, props.averageCalculationMethod]
   );

   const soilPricesForDisplay = useMemo(
      () =>
         calculateSoilSpecificAgriculturalPrices(
            checkedProperties,
            nonAgriToAgriRatio,
            soilFactorsByGroup,
            props.averageCalculationMethod
         ),
      [checkedProperties, nonAgriToAgriRatio, soilFactorsByGroup, props.averageCalculationMethod]
   );

   const pedagogicalBreakdown = useMemo(() => {
      if (!analysisResult || analysisResult.estimatedPricePerSquareMeter === null || !soilPricesForDisplay) {
         return null;
      }

      const alpha = analysisResult.farmlandOverlapPercentage / 100;
      const delta = nonAgriToAgriRatio;
      const soilFractionsByGroup: { [soilGroup: string]: number } = {};
      for (const [soilType, percentage] of Object.entries(analysisResult.soilComposition)) {
         const soilGroup = mapSoilTypeToGroup(soilType);
         if (!soilGroup || !SOIL_QUALITY_GROUPS.includes(soilGroup)) continue;
         soilFractionsByGroup[soilGroup] = (soilFractionsByGroup[soilGroup] ?? 0) + (percentage / 100);
      }

      const totalFraction = Object.values(soilFractionsByGroup).reduce((sum, v) => sum + v, 0);
      if (totalFraction <= 0) return null;

      const normalizationFactor = Math.abs(totalFraction - 1) > 0.01 ? (1 / totalFraction) : 1;
      const rows = Object.entries(soilFractionsByGroup)
         .map(([group, fraction]) => {
            const normalizedFraction = fraction * normalizationFactor;
            const price = soilPricesForDisplay[group as SoilQualityGroup];
            if (price === undefined) return null;
            return {
               group,
               fraction: normalizedFraction,
               price,
               contribution: normalizedFraction * price
            };
         })
         .filter((row): row is { group: string; fraction: number; price: number; contribution: number } => row !== null)
         .sort((a, b) => b.fraction - a.fraction);

      if (rows.length === 0) return null;

      const pAgri = rows.reduce((sum, row) => sum + row.contribution, 0);
      const otherPrice = pAgri * delta;
      const mixFactor = alpha + (1 - alpha) * delta;
      const pTotal = pAgri * mixFactor;

      return {
         alpha,
         delta,
         pAgri,
         otherPrice,
         mixFactor,
         pTotal,
         rows,
         checkedTransactions: checkedProperties.length,
         usedNormalization: Math.abs(totalFraction - 1) > 0.01,
         rawFractionSum: totalFraction
      };
   }, [analysisResult, soilPricesForDisplay, nonAgriToAgriRatio, checkedProperties.length]);

   const displayedMarkPrice = useMemo(() => {
      if (pedagogicalBreakdown?.pAgri && pedagogicalBreakdown.pAgri > 0) {
         return pedagogicalBreakdown.pAgri;
      }
      return avgEffectiveMarkPrice > 0 ? avgEffectiveMarkPrice : null;
   }, [pedagogicalBreakdown, avgEffectiveMarkPrice]);

   const displayedOtherPrice = displayedMarkPrice !== null ? displayedMarkPrice * nonAgriToAgriRatio : null;

   const formatExactNumber = (value: number, maxDigits: number = 6): string => {
      return value.toLocaleString(Localization.getLocale(), {
         minimumFractionDigits: 0,
         maximumFractionDigits: maxDigits
      });
   };
   const formatCompactNumber = (value: number): string => formatExactNumber(value, 3);
   const formatCompactPercent = (value: number): string => `${formatExactNumber(value, 2)}%`;
   const renderAlignedFormulaBlock = (rows: Array<{ left: string; right?: string }>) => (
      <div
         style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            fontSize: "12.5px",
            color: "#2f6e68",
            whiteSpace: "pre-wrap",
            paddingLeft: "2px",
            marginTop: "4px",
            marginBottom: "2px",
            display: "grid",
            gridTemplateColumns: "max-content 16px 1fr",
            columnGap: "4px",
            alignItems: "baseline",
            lineHeight: 1.8
         }}
      >
         {rows.map((row, index) => {
            const isResult = index === rows.length - 1 && row.right !== undefined;
            const resultStyle: React.CSSProperties = isResult ? { color: "#2f6e68", fontWeight: 600 } : {};
            return (
               <React.Fragment key={`${row.left}-${index}`}>
                  <span style={resultStyle}>{row.left}</span>
                  <span style={{ textAlign: "center", color: "#2f6e68", fontWeight: 600 }}>
                     {row.right !== undefined ? "=" : ""}
                  </span>
                  <span style={resultStyle}>{row.right ?? ""}</span>
               </React.Fragment>
            );
         })}
      </div>
   );

   useEffect(() => {
      if (!selectedPolygon || props.referenceProperties.length === 0 || props.checkedItems.size === 0) {
         setGeometryData(null);
         setFarmlandIntersectionGeometry(null);
         setJordbundskortIntersectionGeometry(null);
         setAnalysisResult(null);
         // Clear timeout if component unmounts or dependencies change
         if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
         }
         return;
      }

      // Check if this layer has already failed due to timeout
      if (selectedLayerId !== undefined && failedLayersRef.current.has(selectedLayerId)) {
         setGeometryData({
            polygonArea: 0,
            farmlandOverlapArea: 0,
            farmlandOverlapPercentage: 0,
            soilComposition: {},
            error: "Cannot calculate - calculation timeout"
         });
         setIsFetchingGeometry(false);
         return;
      }

      setIsFetchingGeometry(true);

      const polygonGeometry = selectedPolygon.geometry;
      const polygonAreaM2 = area({
         type: "Feature" as const,
         geometry: polygonGeometry,
         properties: {}
      });


      const MAX_AREA_M2 = 10_000_000;
      if (polygonAreaM2 > MAX_AREA_M2) {
         const areaHa = polygonAreaM2 / 10_000;
         setGeometryData({
            polygonArea: polygonAreaM2,
            farmlandOverlapArea: 0,
            farmlandOverlapPercentage: 0,
            soilComposition: {},
            error: Localization.getFormattedText("Polygon area too large ({area} ha). Maximum allowed: 1000 ha. Please select a smaller polygon.", { area: areaHa.toFixed(1) })
         });
         setIsFetchingGeometry(false);
         return;
      }

      const checkedPropertiesForGeometry = props.referenceProperties.filter((p) => props.checkedItems.has(p.documentID));
      const averageDate = checkedPropertiesForGeometry.length > 0
         ? new Date(checkedPropertiesForGeometry.reduce((sum, p) => sum + p.transactionDate.getTime(), 0) / checkedPropertiesForGeometry.length)
         : new Date();

      // Timeout is configurable to support larger (but valid) polygons.
      const timeoutPromise = new Promise<never>((_, reject) => {
         timeoutRef.current = setTimeout(() => {
            reject(new Error("Calculation timeout - area too large to calculate"));
         }, calculationTimeoutMs);
      });

      (async () => {
         try {

            // Wrap the calculation in Promise.race with timeout
            const calculationPromise = (async () => {
               let farmlandGeometry: any | null = null;
               let farmlandIntersectionResult: { intersectionGeometry: any; overlapPercentage: number } | null = null;

               try {
                  farmlandGeometry = await fetchFarmlandOverlap(
                     polygonGeometry,
                     averageDate
                  );

                  if (farmlandGeometry) {
                     farmlandIntersectionResult = await runWithYield(() => calculateFarmlandIntersection(
                        polygonGeometry as any,
                        farmlandGeometry as any
                     ));
                  }
               } catch (error) {
                  console.error("Error fetching farmland overlap:", error);
               }

               await yieldToMainThread();

               const farmlandOverlapAreaM2 = farmlandIntersectionResult?.intersectionGeometry
                  ? area({
                     type: "Feature" as const,
                     geometry: farmlandIntersectionResult.intersectionGeometry,
                     properties: {}
                  })
                  : 0;

               const farmlandOverlapPercentage = farmlandIntersectionResult?.overlapPercentage ?? 0;

               let soilComposition: { [soilType: string]: number } = {};
               let jordbundskortIntersection: any | null = null;

               try {
                  const jordbundskortGeometry = await fetchJordbundskortOverlap(
                     polygonGeometry
                  );

                  const farmlandIntersectionGeom = farmlandIntersectionResult?.intersectionGeometry;
                  if (farmlandIntersectionGeom && jordbundskortGeometry) {
                     const intersectionResult = await calculateJordbundskortFarmlandIntersection(
                        farmlandIntersectionGeom as any,
                        jordbundskortGeometry as any
                     );

                     if (intersectionResult) {
                        soilComposition = intersectionResult.overlapPercentages;
                        jordbundskortIntersection = intersectionResult.intersectionGeometry;
                     }
                  }
               } catch (error) {
                  console.error("Error fetching jordbundskort overlap:", error);
               }

               await yieldToMainThread();

               return {
                  farmlandIntersectionGeometry: farmlandIntersectionResult?.intersectionGeometry || null,
                  jordbundskortIntersection: jordbundskortIntersection || null,
                  geometryData: {
                     polygonArea: polygonAreaM2,
                     farmlandOverlapArea: farmlandOverlapAreaM2,
                     farmlandOverlapPercentage: farmlandOverlapPercentage,
                     soilComposition: soilComposition
                  }
               };
            })();

            // Race between calculation and timeout
            const result = await Promise.race([calculationPromise, timeoutPromise]);

            // Clear timeout if calculation completed successfully
            if (timeoutRef.current) {
               clearTimeout(timeoutRef.current);
               timeoutRef.current = null;
            }

            setFarmlandIntersectionGeometry(result.farmlandIntersectionGeometry);
            setJordbundskortIntersectionGeometry(result.jordbundskortIntersection);
            setGeometryData(result.geometryData);
         } catch (error: any) {
            // Clear timeout ref
            if (timeoutRef.current) {
               clearTimeout(timeoutRef.current);
               timeoutRef.current = null;
            }

            // If it's a timeout error, mark this layer as failed
            if (error.message && error.message.includes("timeout")) {
               if (selectedLayerId !== undefined) {
                  failedLayersRef.current.add(selectedLayerId);
               }
               console.warn("Calculation timeout for layer:", selectedLayerId);
               setGeometryData({
                  polygonArea: 0,
                  farmlandOverlapArea: 0,
                  farmlandOverlapPercentage: 0,
                  soilComposition: {},
                  error: "Cannot calculate - calculation timeout"
               });
            } else {
               console.error("Error fetching geometry data:", error);
               setGeometryData({
                  polygonArea: 0,
                  farmlandOverlapArea: 0,
                  farmlandOverlapPercentage: 0,
                  soilComposition: {},
                  error: error.message || "Unknown error"
               });
            }
         } finally {
            setIsFetchingGeometry(false);
         }
      })();
   }, [selectedPolygon, selectedLayerId, calculationTimeoutMs]);

   useEffect(() => {
      if (selectedLayerId === undefined) return
      ActionTracker.track("JPBModule", "Action", "SelectLayerForSimulation");
   }, [selectedLayerId]);

   useEffect(() => {
      if (!selectedPolygon || selectedLayerId === undefined) return;
      const polygonAreaM2 = area({
         type: "Feature" as const,
         geometry: selectedPolygon.geometry,
         properties: {}
      });
      const geometry = selectedPolygon.geometry as any;
      const vertexCount = geometry?.type === "Polygon"
         ? (geometry.coordinates?.[0]?.length ?? 0)
         : geometry?.type === "MultiPolygon"
            ? (geometry.coordinates || []).reduce((sum: number, polygon: any) => sum + (polygon?.[0]?.length ?? 0), 0)
            : 0;
      const polygonSignature = `${selectedLayerId}|${selectedPolygon.geometry?.type ?? "unknown"}|${Math.round(polygonAreaM2)}|${vertexCount}`;
      if (trackedPolygonSignatureRef.current === polygonSignature) {
         return;
      }
      trackedPolygonSignatureRef.current = polygonSignature;
      ActionTracker.track("JPBModule", "Action", "UsePolygonForSimulation", {
         layerID: String(selectedLayerId),
         layerType: String(selectedLayer?.type ?? ""),
         mode: `polygonType=${selectedPolygon.geometry?.type ?? "unknown"};areaM2=${Math.round(polygonAreaM2)};vertices=${vertexCount}`
      });
   }, [selectedPolygon, selectedLayerId, selectedLayer?.type]);

   // Cleanup timeout and reset failed layers cache when layer changes
   useEffect(() => {
      // Clear failed layers cache when switching layers - allows retry with different polygon
      failedLayersRef.current.clear();

      return () => {
         if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
         }
      };
   }, [selectedLayerId]);

   useEffect(() => {
      if (!geometryData || checkedProperties.length === 0) {
         setAnalysisResult(null);
         return;
      }

      setIsCalculating(true);
      let cancelled = false;

      (async () => {
         await yieldToMainThread();

         try {
            const soilPrices = calculateSoilSpecificAgriculturalPrices(
               checkedProperties,
               nonAgriToAgriRatio,
               soilFactorsByGroup,
               props.averageCalculationMethod
            );
            await yieldToMainThread();

            let estimatedPrice: number | null = null;
            if (soilPrices && geometryData.farmlandOverlapPercentage > 0 && Object.keys(geometryData.soilComposition).length > 0) {
               try {
                  const agriFraction = geometryData.farmlandOverlapPercentage / 100;
                  const soilCompositionByGroup: { [soilGroup: string]: number } = {};

                  for (const [soilType, percentage] of Object.entries(geometryData.soilComposition)) {
                     const soilGroup = mapSoilTypeToGroup(soilType);
                     if (soilGroup && SOIL_QUALITY_GROUPS.includes(soilGroup)) {
                        const fraction = percentage / 100;
                        if (soilCompositionByGroup[soilGroup]) {
                           soilCompositionByGroup[soilGroup] += fraction;
                        } else {
                           soilCompositionByGroup[soilGroup] = fraction;
                        }
                     }
                  }

                  const totalFraction = Object.values(soilCompositionByGroup).reduce((a, b) => a + b, 0);
                  if (totalFraction > 0 && Math.abs(totalFraction - 1) > 0.01) {
                     const normalizationFactor = 1 / totalFraction;
                     for (const group in soilCompositionByGroup) {
                        soilCompositionByGroup[group] *= normalizationFactor;
                     }
                  }

                  await yieldToMainThread();

                  const hasAllPrices = Object.keys(soilCompositionByGroup).every(
                     group => soilPrices[group as SoilQualityGroup] !== undefined
                  );

                  if (hasAllPrices && Object.keys(soilCompositionByGroup).length > 0) {
                     estimatedPrice = calculateAreaPrice(
                        agriFraction,
                        soilCompositionByGroup,
                        soilPrices as { [soilGroup: string]: number },
                        nonAgriToAgriRatio
                     );
                  }
               } catch (e) {
                  console.warn("Failed to calculate estimated price:", e);
               }
            }

            if (cancelled) return;
            setAnalysisResult({
               polygonArea: geometryData.polygonArea,
               farmlandOverlapArea: geometryData.farmlandOverlapArea,
               farmlandOverlapPercentage: geometryData.farmlandOverlapPercentage,
               soilComposition: geometryData.soilComposition,
               estimatedPricePerSquareMeter: estimatedPrice,
               error: geometryData.error
            });
         } catch (error: any) {
            if (cancelled) return;
            console.error("Error calculating price:", error);
            setAnalysisResult({
               polygonArea: geometryData.polygonArea,
               farmlandOverlapArea: geometryData.farmlandOverlapArea,
               farmlandOverlapPercentage: geometryData.farmlandOverlapPercentage,
               soilComposition: geometryData.soilComposition,
               estimatedPricePerSquareMeter: null,
               error: error.message || "Unknown error"
            });
         } finally {
            if (!cancelled) {
               setIsCalculating(false);
            }
         }
      })();

      return () => {
         cancelled = true;
      };
   }, [
      geometryData,
      checkedProperties,
      nonAgriToAgriRatio,
      soilFactorsByGroup,
      props.averageCalculationMethod,
      props.customAssumptions,
      props.settingsRefreshToken
   ]);

   useEffect(() => {
      if (!mapitState.map) return;
      const map = mapitState.map.getMapPop();
      if (!map) return;


      const farmlandGeoJson = farmlandIntersectionGeometry
         ? {
            type: "FeatureCollection" as const,
            features: [{
               type: "Feature" as const,
               geometry: farmlandIntersectionGeometry,
               properties: {}
            }]
         }
         : {
            type: "FeatureCollection" as const,
            features: []
         };

      if (map.getSource(SOURCE_ID_POLYGON_ANALYZER_FARMLAND)) {
         const source = map.getSource(SOURCE_ID_POLYGON_ANALYZER_FARMLAND) as GeoJSONSource;
         if (source) source.setData(farmlandGeoJson as any);
      } else {
         map.addSource(SOURCE_ID_POLYGON_ANALYZER_FARMLAND, {
            type: 'geojson',
            data: farmlandGeoJson as any
         });
      }

      if (!map.getLayer(LAYER_ID_POLYGON_ANALYZER_FARMLAND)) {
         const farmlandLayerSpec: any = {
            id: LAYER_ID_POLYGON_ANALYZER_FARMLAND,
            type: 'fill',
            source: SOURCE_ID_POLYGON_ANALYZER_FARMLAND,
            paint: {
               'fill-pattern': "mit-patt-char-🚜-1",
               'fill-opacity': ["interpolate", ["linear"], ["zoom"], 13, 0, 14, 0.6]
            }
         };
         map.addLayer(farmlandLayerSpec);
      }

      if (!map.getLayer(LAYER_ID_POLYGON_ANALYZER_FARMLAND + "-outline")) {
         const farmlandOutlineLayerSpec: any = {
            id: LAYER_ID_POLYGON_ANALYZER_FARMLAND + "-outline",
            type: 'line',
            source: SOURCE_ID_POLYGON_ANALYZER_FARMLAND,
            paint: {
               'line-color': '#ff6b00',
               'line-opacity': ["interpolate", ["linear"], ["zoom"], 12.5, 0, 13.5, 1],
               'line-width': 2
            }
         };
         map.addLayer(farmlandOutlineLayerSpec);
      }

      const jordbundskortGeoJson = jordbundskortIntersectionGeometry
         ? (jordbundskortIntersectionGeometry.type === "FeatureCollection"
            ? jordbundskortIntersectionGeometry
            : {
               type: "FeatureCollection" as const,
               features: [{
                  type: "Feature" as const,
                  geometry: jordbundskortIntersectionGeometry,
                  properties: {}
               }]
            })
         : {
            type: "FeatureCollection" as const,
            features: []
         };

      if (jordbundskortGeoJson.type === "FeatureCollection" && jordbundskortGeoJson.features) {
         jordbundskortGeoJson.features = jordbundskortGeoJson.features.map((feature: any) => ({
            ...feature,
            properties: {
               ...feature.properties,
               color: getSoilTypeColor(feature.properties?.Jordtype || feature.properties?.jordtype)
            }
         }));
      }

      if (map.getSource(SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT)) {
         const source = map.getSource(SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT) as GeoJSONSource;
         if (source) source.setData(jordbundskortGeoJson as any);
      } else {
         map.addSource(SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT, {
            type: 'geojson',
            data: jordbundskortGeoJson as any
         });
      }

      if (!map.getLayer(LAYER_ID_POLYGON_ANALYZER_JORDBUNDSKORT)) {
         const jordbundskortLayerSpec: any = {
            id: LAYER_ID_POLYGON_ANALYZER_JORDBUNDSKORT,
            type: 'fill',
            source: SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT,
            paint: {
               'fill-color': ['get', 'color'],
               'fill-opacity': ["interpolate", ["linear"], ["zoom"], 13, 0, 14, 0.7],
               'fill-antialias': false,
               'fill-outline-color': '#00000000'
            }
         };
         map.addLayer(jordbundskortLayerSpec);
      }

      return () => {
         if (map.getLayer(LAYER_ID_POLYGON_ANALYZER_JORDBUNDSKORT)) {
            map.removeLayer(LAYER_ID_POLYGON_ANALYZER_JORDBUNDSKORT);
         }
         if (map.getLayer(LAYER_ID_POLYGON_ANALYZER_FARMLAND + "-outline")) {
            map.removeLayer(LAYER_ID_POLYGON_ANALYZER_FARMLAND + "-outline");
         }
         if (map.getLayer(LAYER_ID_POLYGON_ANALYZER_FARMLAND)) {
            map.removeLayer(LAYER_ID_POLYGON_ANALYZER_FARMLAND);
         }
         if (map.getSource(SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT)) {
            map.removeSource(SOURCE_ID_POLYGON_ANALYZER_JORDBUNDSKORT);
         }
         if (map.getSource(SOURCE_ID_POLYGON_ANALYZER_FARMLAND)) {
            map.removeSource(SOURCE_ID_POLYGON_ANALYZER_FARMLAND);
         }
      };
   }, [mapitState.map, farmlandIntersectionGeometry, jordbundskortIntersectionGeometry]);

   return (
      <div className="jpb-polygon-analyzer-root">
         <div className="jpb-card jpb-card--padded" style={{ overflow: "hidden" }}>
            <h3 style={{ marginTop: 0, marginBottom: "15px", fontSize: "18px", fontWeight: "600" }}>
               {Localization.getText("Polygon Price Analyzer")}
            </h3>

            {polygonLayers.length > 0 ? (
               <GlassInputGroup>
                  <div className="GlassInputLabel" style={{ marginBottom: "10px" }}>
                     {Localization.getText("Select Polygon Layer")}
                  </div>
                  <div style={{ marginBottom: "15px" }}>
                     <SelectLayer
                        selectedLayerId={selectedLayerId || ""}
                        onSelect={(layerId) => {
                           setSelectedLayerId(layerId);
                        }}
                        availableLayers={polygonLayers}
                     />
                  </div>
               </GlassInputGroup>
            ) : (
               <div style={{ marginTop: "5px", color: "#666", fontStyle: "italic" }}>
                  {Localization.getText("Create or define a polygon layer to use the analyzer")}
               </div>
            )}

            {(isFetchingGeometry || (isCalculating && !analysisResult)) && (
               <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "15px" }}>
                  <Spinner size="sm" />
                  <span>
                     {isFetchingGeometry
                        ? Localization.getText("Fetching geometry data...")
                        : Localization.getText("Calculating price...")}
                  </span>
               </div>
            )}

            {analysisResult && !isFetchingGeometry && (
               <div style={{ marginTop: "20px" }}>
                  {analysisResult.error ? (
                     <div style={{ color: "#d32f2f", marginBottom: "10px", padding: "10px", backgroundColor: "#ffebee", borderRadius: "4px", border: "1px solid #ffcdd2" }}>
                        <strong>{Localization.getText("Error")}:</strong> {analysisResult.error}
                     </div>
                  ) : (
                     <>
                        {analysisResult.farmlandOverlapPercentage > 0 && (
                           <SoilDistributionBar
                              analysisResult={analysisResult}
                              hoveredSoilType={hoveredSoilType}
                              hoveredSoilPercentage={hoveredSoilPercentage}
                              hoveredNonFarmland={hoveredNonFarmland}
                              setHoveredSoilType={setHoveredSoilType}
                              setHoveredSoilPercentage={setHoveredSoilPercentage}
                              setHoveredNonFarmland={setHoveredNonFarmland}
                           />
                        )}
                        <div style={{
                           marginTop: "20px",
                           border: "1px solid #ddd",
                           borderRadius: "4px",
                           overflow: "hidden"
                        }}>
                           <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                              <thead>
                                 <tr style={{ backgroundColor: "#f5f5f5", borderBottom: "2px solid #ddd" }}>
                                    <th style={{ padding: "10px", textAlign: "left", fontWeight: "600" }}>
                                       {Localization.getText("Category")}
                                    </th>
                                    <th style={{ padding: "10px", textAlign: "right", fontWeight: "600" }}>
                                       {Localization.getText("M_Area")}
                                    </th>
                                    <th style={{ padding: "10px", textAlign: "right", fontWeight: "600" }}>
                                       {Localization.getText("Percentage")}
                                    </th>
                                    <th style={{ padding: "10px", textAlign: "right", fontWeight: "600" }}>
                                       {Localization.getText("Price per")} {props.areaUnit === AreaUnit.Hectare ? "ha" : "m²"}
                                    </th>
                                 </tr>
                              </thead>
                              <tbody>
                                 <tr style={{ borderBottom: "1px solid #eee", backgroundColor: "#fff" }}>
                                    <td style={{ padding: "10px", fontWeight: "600" }}>
                                       {Localization.getText("Total Polygon Area")}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {formatArea(analysisResult.polygonArea, props.areaUnit)}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {formatPercentage(100)}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       -
                                    </td>
                                 </tr>

                                 <tr style={{ borderBottom: "1px solid #eee", backgroundColor: "#fff" }}>
                                    <td style={{ padding: "10px", fontWeight: "600", paddingLeft: "20px" }}>
                                       {Localization.getText("Farmland (Mark)")}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {formatArea(analysisResult.farmlandOverlapArea, props.areaUnit)}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {formatPercentage(analysisResult.farmlandOverlapPercentage)}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {displayedMarkPrice !== null
                                          ? formatPricePerSquareMeter(displayedMarkPrice, props.areaUnit)
                                          : "-"}
                                    </td>
                                 </tr>

                                 {Object.entries(analysisResult.soilComposition)
                                    .sort(([a], [b]) => {
                                       const groupA = mapSoilTypeToGroup(a);
                                       const groupB = mapSoilTypeToGroup(b);
                                       if (!groupA || !groupB) return 0;
                                       const indexA = soilFactorsByGroup[groupA] || 0;
                                       const indexB = soilFactorsByGroup[groupB] || 0;
                                       return indexB - indexA;
                                    })
                                    .map(([soilType, percentage]) => {
                                       const soilGroup = mapSoilTypeToGroup(soilType);
                                       const soilAreaM2 = (analysisResult.farmlandOverlapArea * percentage) / 100;
                                       const percentageOfTotal = (analysisResult.farmlandOverlapPercentage * percentage) / 100;
                                       const soilPrice = soilGroup && soilPricesForDisplay ? soilPricesForDisplay[soilGroup] : undefined;

                                       return (
                                          <tr key={soilType} style={{ borderBottom: "1px solid #eee", backgroundColor: "#fff" }}>
                                             <td style={{ padding: "10px", paddingLeft: "40px", fontStyle: "italic" }}>
                                                {soilType} {soilGroup && `(${soilGroup})`}
                                             </td>
                                             <td style={{ padding: "10px", textAlign: "right" }}>
                                                {formatArea(soilAreaM2, props.areaUnit)}
                                             </td>
                                             <td style={{ padding: "10px", textAlign: "right" }}>
                                                {formatPercentage(percentageOfTotal)}
                                             </td>
                                             <td style={{ padding: "10px", textAlign: "right" }}>
                                                {soilPrice !== undefined
                                                   ? formatPricePerSquareMeter(soilPrice, props.areaUnit)
                                                   : "-"}
                                             </td>
                                          </tr>
                                       );
                                    })}

                                 <tr style={{ borderBottom: "1px solid #eee", backgroundColor: "#fff" }}>
                                    <td style={{ padding: "10px", fontWeight: "600", paddingLeft: "20px" }}>
                                       {Localization.getText("Non-Farmland (Non-Mark)")}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {formatArea(analysisResult.polygonArea - analysisResult.farmlandOverlapArea, props.areaUnit)}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {formatPercentage(100 - analysisResult.farmlandOverlapPercentage)}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right" }}>
                                       {displayedOtherPrice !== null
                                          ? formatPricePerSquareMeter(displayedOtherPrice, props.areaUnit)
                                          : "-"}
                                    </td>
                                 </tr>

                                 {analysisResult.estimatedPricePerSquareMeter !== null && (
                                    <tr style={{ borderTop: "2px solid #3a8e87", backgroundColor: "#fff" }}>
                                       <td style={{ padding: "15px", fontWeight: "600", fontSize: "16px" }}>
                                          {Localization.getText("Estimated Price per")} {props.areaUnit === AreaUnit.Hectare ? "ha" : "m²"}
                                       </td>
                                       <td colSpan={2} style={{ padding: "15px" }}></td>
                                       <td style={{ padding: "15px", textAlign: "right", fontSize: "16px", fontWeight: "600", color: "#3a8e87" }}>
                                          {formatPricePerSquareMeter(analysisResult.estimatedPricePerSquareMeter, props.areaUnit)}
                                       </td>
                                    </tr>
                                 )}
                              </tbody>
                           </table>
                        </div>

                        {pedagogicalBreakdown && (
                           <details style={{ marginTop: "14px", border: "1px solid #d8e8e6", borderRadius: "6px", background: "#f8fcfb" }}>
                              <summary style={{ cursor: "pointer", padding: "10px 12px", fontWeight: 600, color: "#2f6e68" }}>
                                 {Localization.getText("How this estimate is calculated")}
                              </summary>
                              <div style={{ padding: "4px 14px 14px 14px", fontSize: "13px", lineHeight: 1.55 }}>
                                 <div style={{ marginBottom: "14px", paddingBottom: "12px", borderBottom: "1px solid #e3efed" }}>
                                    <div style={{ fontWeight: 600, color: "#111", marginBottom: "3px" }}>
                                       {Localization.getText("1) JB prices (where they come from)")}
                                    </div>
                                    <span style={{ color: "#111" }}>
                                       {Localization.getText("Computed from checked reference transactions using the soil model and current assumptions.")}{" "}
                                       ({pedagogicalBreakdown.checkedTransactions} {Localization.getText("transactions")}).
                                    </span>
                                 </div>
                                 <div style={{ marginBottom: "14px", paddingBottom: "12px", borderBottom: "1px solid #e3efed" }}>
                                    <div style={{ fontWeight: 600, color: "#111", marginBottom: "3px" }}>
                                       {Localization.getText("2) Polygon farmland soil mix")}
                                    </div>
                                    <span style={{ color: "#111" }}>
                                       {pedagogicalBreakdown.rows.map((r) => `${r.group}: ${formatCompactPercent(r.fraction * 100)}`).join(" · ")}
                                       {pedagogicalBreakdown.usedNormalization && (
                                          <span> ({Localization.getText("normalized from")} {formatCompactPercent(pedagogicalBreakdown.rawFractionSum * 100)})</span>
                                       )}
                                    </span>
                                 </div>
                                 <div style={{ marginBottom: "14px", paddingBottom: "12px", borderBottom: "1px solid #e3efed" }}>
                                    <div style={{ fontWeight: 600, color: "#111", marginBottom: "3px" }}>
                                       {Localization.getText("3) Farmland base price (P_agri)")}
                                    </div>
                                    <span style={{ color: "#111" }}>{Localization.getText("Average soil-group price for farmland in this polygon.")}</span>
                                    {renderAlignedFormulaBlock([
                                       {
                                          left: Localization.getText("Farmland price"),
                                          right: Localization.getText("average soil-group price")
                                       },
                                       {
                                          left: "",
                                          right: `Σ(${Localization.getText("Jordgruppe andel")} · ${Localization.getText("soil-group price")})`
                                       },
                                       {
                                          left: "",
                                          right: pedagogicalBreakdown.rows.map((r) => `${formatExactNumber(r.fraction, 4)} · ${formatExactNumber(r.price, 3)}`).join(" + ")
                                       },
                                       {
                                          left: "",
                                          right: `${formatExactNumber(pedagogicalBreakdown.pAgri, 3)} (${formatPricePerSquareMeter(pedagogicalBreakdown.pAgri, props.areaUnit)})`
                                       }
                                    ])}
                                 </div>
                                 <div style={{ marginBottom: "14px", paddingBottom: "12px", borderBottom: "1px solid #e3efed" }}>
                                    <div style={{ fontWeight: 600, color: "#111", marginBottom: "3px" }}>
                                       {Localization.getText("4) Non-farmland deduced from ratio")}
                                    </div>
                                    <span style={{ color: "#111" }}>{Localization.getText("Non-farmland price is derived from delta.")}</span>
                                    {renderAlignedFormulaBlock([
                                       {
                                          left: Localization.getText("Non-farmland price"),
                                          right: `${Localization.getText("Farmland price")} · delta`
                                       },
                                       {
                                          left: "",
                                          right: `${formatExactNumber(pedagogicalBreakdown.pAgri, 3)} · ${formatExactNumber(pedagogicalBreakdown.delta, 3)}`
                                       },
                                       {
                                          left: "",
                                          right: `${formatExactNumber(pedagogicalBreakdown.otherPrice, 3)} (${formatPricePerSquareMeter(pedagogicalBreakdown.otherPrice, props.areaUnit)})`
                                       }
                                    ])}
                                 </div>
                                 <div>
                                    <div style={{ fontWeight: 600, color: "#111", marginBottom: "3px" }}>
                                       {Localization.getText("5) Final estimate")}
                                    </div>
                                    <span style={{ color: "#111" }}>{Localization.getText("Final estimate combines farmland share and non-farmland ratio.")}</span>
                                    {renderAlignedFormulaBlock([
                                       {
                                          left: Localization.getText("Total estimated price"),
                                          right: `${Localization.getText("Price on farmland")} · ${Localization.getText("Mark andel")} + ${Localization.getText("Price on non-farmland")} · ${Localization.getText("Ikke-mark andel")}`
                                       },
                                       {
                                          left: "",
                                          right: `${formatExactNumber(pedagogicalBreakdown.pAgri, 3)} · ${formatExactNumber(pedagogicalBreakdown.alpha, 3)} + ${formatExactNumber(pedagogicalBreakdown.otherPrice, 3)} · ${formatExactNumber(1 - pedagogicalBreakdown.alpha, 3)}`
                                       },
                                       {
                                          left: "",
                                          right: `${formatExactNumber(pedagogicalBreakdown.pAgri * pedagogicalBreakdown.alpha, 3)} + ${formatExactNumber(pedagogicalBreakdown.otherPrice * (1 - pedagogicalBreakdown.alpha), 3)}`
                                       },
                                       {
                                          left: "",
                                          right: `${formatExactNumber(pedagogicalBreakdown.pTotal, 3)} (${formatPricePerSquareMeter(pedagogicalBreakdown.pTotal, props.areaUnit)})`
                                       }
                                    ])}
                                    <div style={{
                                       marginTop: "10px",
                                       color: "#111",
                                       fontSize: "12px"
                                    }}>
                                       <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" }}>
                                          {Localization.getText("Andel af mark")} = {formatCompactNumber(pedagogicalBreakdown.alpha)}
                                       </span>
                                       {" · "}
                                       <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" }}>
                                          {Localization.getText("Ikke-mark-forhold")} = {formatCompactNumber(pedagogicalBreakdown.delta)}
                                       </span>
                                    </div>
                                 </div>
                              </div>
                           </details>
                        )}

                        {analysisResult.estimatedPricePerSquareMeter === null && (
                           <div style={{ marginTop: "20px", color: "#666", fontStyle: "italic" }}>
                              {Localization.getText("Unable to calculate estimated price. Ensure the polygon overlaps with farmland and soil data.")}
                           </div>
                        )}
                     </>
                  )}
               </div>
            )}

            {!selectedLayerId && polygonLayers.length > 0 && !isFetchingGeometry && !isCalculating && (
               <div style={{ marginTop: "15px", color: "#666", fontStyle: "italic" }}>
                  {Localization.getText("Select a polygon layer to analyze")}
               </div>
            )}
         </div>
      </div>
   );
}